import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/utils/cn'
import { NavIconTile } from './NavIconTile'
import { getSectionNavStyles, type NavSectionKey } from './navIconStyles'
import type { NavItemAccess } from '@/config/routeAccess'

export type NavLinkItem = NavItemAccess & {
  icon: React.ElementType
  label: string
}

function useIsNavActive(to: string) {
  const location = useLocation()
  const currentPath = location.pathname + location.search

  if (to === '/dashboard') return location.pathname === '/dashboard'
  if (to === '/cs') return location.pathname === '/cs'
  if (to.startsWith('/cs/')) {
    return location.pathname === to || location.pathname.startsWith(`${to}/`)
  }
  if (to === '/empresas') {
    return location.pathname === '/empresas' && !location.search.includes('view=contatos')
  }
  if (to === '/empresas?view=contatos') {
    return location.pathname === '/empresas' && location.search.includes('view=contatos')
  }
  return location.pathname === to || currentPath === to
}

export function SidebarItem({
  icon: Icon,
  label,
  to,
  sectionKey,
  collapsed,
  onNavigate,
  isDark,
}: NavLinkItem & {
  sectionKey: NavSectionKey
  collapsed: boolean
  onNavigate?: () => void
  isDark: boolean
}) {
  const isActive = useIsNavActive(to)
  const sectionStyles = getSectionNavStyles(sectionKey, isDark)

  return (
    <NavLink to={to} end={to === '/dashboard'} title={label} aria-label={label} onClick={onNavigate}>
      <div
        className={cn(
          'flex items-center rounded-xl text-[13px] font-medium transition-all',
          collapsed ? 'justify-center px-2 py-2.5' : 'gap-2.5 py-2.5',
          !collapsed && (isActive ? 'border-l-[3px] pl-[9px] pr-3' : 'px-3'),
          isActive && 'shadow-none',
          !isActive &&
            (isDark
              ? 'text-text-muted hover:bg-white/[0.04] hover:text-text-primary'
              : 'text-[#64748b] hover:bg-white/70 hover:text-[#334155]'),
        )}
        style={
          isActive
            ? {
                color: isDark ? 'var(--text-sidebar)' : '#334155',
                backgroundColor: sectionStyles.activeBg,
                borderLeftColor: collapsed ? 'transparent' : sectionStyles.activeBorder,
              }
            : isDark
              ? { color: 'var(--text-muted)' }
              : { color: '#64748b' }
        }
      >
        <NavIconTile
          icon={Icon}
          sectionKey={sectionKey}
          isActive={isActive}
          title={collapsed ? label : undefined}
        />
        {!collapsed && (
          <span className={cn('truncate leading-none', isActive && !isDark && 'font-semibold text-[#334155]')}>
            {label}
          </span>
        )}
      </div>
    </NavLink>
  )
}
